import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { SimpleLoaderComponent } from '@prlw/common/simple-loader/simple-loader.component';
import { NumberFormatModule } from '@prlw/libs/number-format/number-format.module';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import {
  ColorType,
  IChartApi,
  ISeriesApi,
  MouseEventParams,
  OhlcData,
  SingleValueData,
  Time,
  createChart,
} from 'lightweight-charts';
import { BehaviorSubject, map } from 'rxjs';

export type TChartData = OhlcData &
  SingleValueData & {
    avgPrice: number;
  };

@Component({
  selector: 'prlw-tw-price-history-chart',
  standalone: true,
  imports: [CommonModule, NumberFormatModule, SimpleLoaderComponent],
  templateUrl: './tw-price-history-chart.component.html',
  styleUrl: './tw-price-history-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwPriceHistoryChartComponent implements AfterViewInit, OnChanges {
  @Input() chartData: TChartData[] | null = null;
  @Input() showLegend: boolean | null = true;
  @Input() ticker: string | null = null;

  @ViewChild('twChart') el: ElementRef | null = null;

  constructor(private readonly responsiveService: ResponsiveService) {}

  xsmall$ = this.responsiveService.xsmall$;

  chart: IChartApi | null = null;
  candlestick: ISeriesApi<'Candlestick'> | null = null;
  histogram: ISeriesApi<'Histogram'> | null = null;

  formatter = Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  crosshairMove$ = new BehaviorSubject<MouseEventParams<Time> | null>(null);
  candlestickLegend$ = this.crosshairMove$.pipe(
    map((item) => {
      if (!this.chartData) return null;
      if (!item?.logical) return null;
      if (!this.candlestick) return null;
      if (!item.seriesData.get(this.candlestick)) return null;
      return {
        ...item.seriesData.get(this.candlestick),
        avgPrice: this.chartData[item.logical].avgPrice,
        diff:
          this.chartData[item.logical].close -
          this.chartData[item.logical - 1].close,
        percentage:
          (this.chartData[item.logical].close /
            this.chartData[item.logical - 1].close -
            1) *
          100,
      } as OhlcData & { avgPrice: number; diff: number; percentage: number };
    }),
  );

  ngAfterViewInit(): void {
    if (this.el) {
      this.chart = createChart(this.el.nativeElement, {
        handleScroll: this.xsmall$.getValue()
          ? {
              mouseWheel: false,
              pressedMouseMove: false,
              horzTouchDrag: true,
              vertTouchDrag: false,
            }
          : true,
        autoSize: true,
        layout: {
          textColor: 'black',
          background: { type: ColorType.Solid, color: 'white' },
        },
        leftPriceScale: {
          visible: true,
        },
        grid: {
          vertLines: { color: '#fff' },
          horzLines: { color: '#fff' },
        },
      });
      this.chart.priceScale('right').applyOptions({
        borderVisible: false,
      });
      this.chart.priceScale('left').applyOptions({
        borderVisible: false,
      });
      this.chart.timeScale().applyOptions({
        borderVisible: false,
      });

      this.candlestick = this.chart.addCandlestickSeries({
        priceScaleId: 'right',
        priceFormat: {
          type: 'custom',
          formatter: (price: number) => this.formatter.format(price),
        },
      });

      this.candlestick.setData(this.chartData ?? []);

      this.histogram = this.chart.addHistogramSeries({
        color: 'rgba(128, 128, 128, 0.1)',
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: 'left',
      });
      this.histogram.priceScale().applyOptions({
        scaleMargins: {
          top: 0.7,
          bottom: 0,
        },
      });
      this.histogram.setData(this.chartData ?? []);

      this.chart.timeScale().fitContent();

      this.chart.subscribeCrosshairMove((param) => {
        if ((param.point?.y ?? 0) < 40) {
          this.crosshairMove$.next(null);
          return;
        }
        this.crosshairMove$.next(param);
      });
    }
  }

  ngOnChanges(): void {
    if (this.chart) {
      if (this.candlestick) {
        this.candlestick.setData(this.chartData ?? []);
        this.chart.timeScale().applyOptions({
          barSpacing: 14,
        });
      }
      if (this.histogram) {
        this.histogram.setData(this.chartData ?? []);
      }
    }
  }

  getColor(open: number, close: number) {
    return open > close ? 'red' : open < close ? 'green' : 'grey';
  }

  displaySign(value: number) {
    if (value > 0) return '+';
    if (value < 0) return '';
    return '';
  }
}
